import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';

import { api } from '@/lib/api';
import { useSocketEvent } from '@/lib/useSocket';
import type { NotificationView } from '@shared/types';
import { formatRelative } from '@/lib/time';
import { locale } from '@/lib/i18n';
import { PushInvite } from './PushToggle';

export function NotificationsMenu() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationView[]>([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const load = () => {
    api
      .get<{ items: NotificationView[]; unread: number }>('/notifications')
      .then((data) => {
        setItems(data.items);
        setUnread(data.unread);
      })
      .catch(() => undefined);
  };

  useEffect(load, []);

  // Las notificaciones nuevas llegan por WebSocket, sin recargar la página.
  useSocketEvent<NotificationView>('notification', (notification) => {
    setItems((current) => [notification, ...current].slice(0, 50));
    setUnread((n) => n + 1);
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const openMenu = () => {
    setOpen((value) => {
      const next = !value;
      if (next && unread > 0) {
        // Marcar como leídas al abrir, igual que en cualquier bandeja.
        void api.post('/notifications/read').then(() => setUnread(0));
        setItems((list) => list.map((n) => ({ ...n, read: true })));
      }
      return next;
    });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="btn btn-icon border-white/15 bg-white/[0.08] text-white hover:bg-white/15 hover:text-white relative transition-all hover:scale-105 active:scale-95"
        onClick={openMenu}
        aria-label={unread > 0 ? `Notificaciones (${unread} sin leer)` : 'Notificaciones'}
        aria-expanded={open}
      >
        <Bell size={18} />
        {unread > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="animate-pop-in absolute right-0 mt-1 max-h-96 w-80 overflow-y-auto popover">
          <div className="border-b border-border px-3 py-2 text-sm font-medium">
            Notificaciones
          </div>

          {items.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-faint">
              Aún no tienes notificaciones.
            </p>
          ) : (
            <ul>
              {items.map((item) => (
                <li key={item.id} className="border-b border-border last:border-0">
                  <NotificationRow item={item} onNavigate={() => setOpen(false)} />
                </li>
              ))}
            </ul>
          )}

          <PushInvite />
        </div>
      ) : null}
    </div>
  );
}

function NotificationRow({
  item,
  onNavigate,
}: {
  item: NotificationView;
  onNavigate: () => void;
}) {
  const payload = item.payload as {
    username?: string;
    challengeId?: string;
    description?: string;
    title?: string;
    approved?: boolean;
    decision?: string;
    topicId?: string;
    page?: number;
    simulId?: string;
    tournamentId?: string;
    gameId?: string;
    name?: string;
    message?: string;
    opponent?: string;
    san?: string;
    deadline?: number;
    slug?: string;
    accepted?: boolean;
    invitedBy?: string;
    seasonStarted?: boolean;
    madeAdmin?: boolean;
    champion?: boolean;
    associationInvite?: string;
    clubAccepted?: string;
    clubDeclined?: string;
    clubLeft?: string;
  };
  const when = formatRelative(item.createdAt);

  if (item.type === 'challenge' && payload.username) {
    return (
      <Link
        to="/desafios"
        onClick={onNavigate}
        className="block px-3 py-2.5 transition-colors hover:bg-hover"
      >
        <span className="text-sm">
          <strong className="font-medium">{payload.username}</strong> te ha retado.
        </span>
        {payload.description ? (
          <span className="mt-0.5 block text-xs text-muted">{payload.description}</span>
        ) : null}
        <span className="mt-0.5 block text-xs text-faint">{when}</span>
      </Link>
    );
  }

  if (item.type === 'title') {
    return (
      <div className="px-3 py-2.5">
        <span className="text-sm">
          {payload.approved
            ? `Tu titulo ${payload.title} esta verificado: ya aparece junto a tu nombre.`
            : `Tu solicitud de titulo ${payload.title} no se ha aprobado.`}
        </span>
        {payload.decision ? (
          <span className="mt-0.5 block text-xs text-muted">{payload.decision}</span>
        ) : null}
        <span className="mt-0.5 block text-xs text-faint">{when}</span>
      </div>
    );
  }

  if (item.type === 'follow' && payload.username) {
    return (
      <Link
        to={`/@/${payload.username}`}
        onClick={onNavigate}
        className="block px-3 py-2.5 transition-colors hover:bg-hover"
      >
        <span className="text-sm">
          <strong className="font-medium">{payload.username}</strong> te sigue.
        </span>
        <span className="mt-0.5 block text-xs text-faint">{when}</span>
      </Link>
    );
  }

  const linked = (to: string, text: ReactNode) => (
    <Link
      to={to}
      onClick={onNavigate}
      className="block px-3 py-2.5 transition-colors hover:bg-hover"
    >
      <span className="text-sm">{text}</span>
      <span className="mt-0.5 block text-xs text-faint">{when}</span>
    </Link>
  );

  if (item.type === 'forumReply' && payload.topicId) {
    const page = payload.page && payload.page > 1 ? `?pagina=${payload.page}` : '';
    return linked(
      `/foro/hilo/${payload.topicId}${page}`,
      <>
        <strong className="font-medium">{payload.username}</strong> ha respondido en «{payload.title}».
      </>,
    );
  }

  if (item.type === 'forumMention' && payload.topicId) {
    return linked(
      `/foro/hilo/${payload.topicId}`,
      <>
        <strong className="font-medium">{payload.username}</strong> te ha mencionado en «{payload.title}».
      </>,
    );
  }

  if (item.type === 'simulInvite' && payload.simulId) {
    return linked(
      `/simultanea/${payload.simulId}`,
      <>
        <strong className="font-medium">{payload.username}</strong> te invita a su simultánea «{payload.name}».
      </>,
    );
  }

  if (item.type === 'simulAccepted' && payload.simulId) {
    return linked(`/simultanea/${payload.simulId}`, <>Tienes tablero en la simultánea «{payload.name}».</>);
  }

  if (item.type === 'simulStart' && payload.gameId) {
    return linked(
      `/partida/${payload.gameId}`,
      <>La simultánea «{payload.name}» ha empezado: ve a tu tablero.</>,
    );
  }

  if (item.type === 'tournamentSoon' && payload.tournamentId) {
    return linked(
      `/torneo/${payload.tournamentId}`,
      <>
        <strong className="font-medium">{payload.name}</strong> empieza en 5 minutos.
      </>,
    );
  }

  if (item.type === 'corrTurn' && payload.gameId) {
    return linked(
      `/partida/${payload.gameId}`,
      <>
        Te toca contra <strong className="font-medium">{payload.opponent}</strong>
        {payload.san ? <> (jugó {payload.san})</> : null}.
      </>,
    );
  }

  if (item.type === 'corrDeadline' && payload.gameId) {
    return linked(
      `/partida/${payload.gameId}`,
      <>
        Se acaba tu plazo contra <strong className="font-medium">{payload.opponent}</strong>
        {payload.deadline ? <> el {new Date(payload.deadline).toLocaleString(locale(), { dateStyle: 'short', timeStyle: 'short' })}</> : null}.
      </>,
    );
  }

  if (item.type === 'teamJoin' && payload.slug) {
    const club = <strong className="font-medium">{payload.name}</strong>;
    return linked(
      `/comunidad/${payload.slug}`,
      payload.champion ? (
        <>🏆 Eres el nuevo campeón de {club}.</>
      ) : payload.madeAdmin ? (
        <>Ahora eres administrador de {club}.</>
      ) : payload.seasonStarted ? (
        <>Empieza una temporada de liga en {club}.</>
      ) : payload.associationInvite ? (
        <>
          <strong className="font-medium">{payload.associationInvite}</strong> invita a {club} a unirse a la asociación.
        </>
      ) : payload.clubAccepted ? (
        <>
          <strong className="font-medium">{payload.clubAccepted}</strong> se ha unido a {club}.
        </>
      ) : payload.clubDeclined ? (
        <>
          <strong className="font-medium">{payload.clubDeclined}</strong> ha rechazado unirse a {club}.
        </>
      ) : payload.clubLeft ? (
        <>
          <strong className="font-medium">{payload.clubLeft}</strong> ha dejado {club}.
        </>
      ) : payload.accepted ? (
        <>Te han aceptado en {club}.</>
      ) : payload.invitedBy ? (
        <>
          <strong className="font-medium">{payload.invitedBy}</strong> te ha añadido a {club}.
        </>
      ) : payload.username ? (
        <>
          <strong className="font-medium">{payload.username}</strong> pide entrar en {club}.
        </>
      ) : (
        <>Novedades en {club}.</>
      ),
    );
  }

  if (item.type === 'warning') {
    return (
      <div className="border-l-2 border-warn px-3 py-2.5">
        <span className="text-sm">
          <strong className="font-medium text-warn">Aviso de la moderación:</strong> {payload.message}
        </span>
        <span className="mt-0.5 block text-xs text-faint">{when}</span>
      </div>
    );
  }

  if (item.type === 'reportResolved') {
    return (
      <div className="px-3 py-2.5">
        <span className="text-sm">
          Gracias por tu denuncia sobre <strong className="font-medium">{payload.username}</strong>: hemos tomado medidas.
        </span>
        <span className="mt-0.5 block text-xs text-faint">{when}</span>
      </div>
    );
  }

  return (
    <div className="px-3 py-2.5">
      <span className="text-sm text-muted">Novedad en tu cuenta.</span>
      <span className="mt-0.5 block text-xs text-faint">{when}</span>
    </div>
  );
}
